import { useFeatureFlags } from "@/core/auth/hooks";
import { tKey, type I18nKey } from "@/core/i18n";
import { Button } from "@/modules/shadcn/ui/button";
import { Spinner } from "@/modules/shadcn/ui/spinner";
import { Tabs, TabsList, TabsTrigger } from "@/modules/shadcn/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/modules/shadcn/ui/tooltip";
import type { TFunction } from "i18next";
import { Play, Square } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  NO_MODEL_MESSAGE,
  type DeployPhase,
  type DeployProgress,
} from "../../hooks/useRunDeploy";

export type RunTab = "inference" | "dashboard" | "reports";

interface RunSubheaderProps {
  activeTab: RunTab;
  onTabChange: (tab: RunTab) => void;
  onDeploy: () => void;
  onStop: () => void;
  deployPhase: DeployPhase;
  /** Camera-side transfer progress; null on the upload fallback, which has none. */
  deployProgress?: DeployProgress | null;
  canDeploy: boolean;
  /** False greys out Deploy (via canDeploy) and says why on hover. */
  hasModel: boolean;
  isDeployed: boolean;
  configSelector?: ReactNode;
}

const TABS: { key: RunTab; label: I18nKey<"run"> }[] = [
  { key: "inference", label: tKey("run:tabs.inference") },
  { key: "dashboard", label: tKey("run:tabs.dashboard") },
  { key: "reports", label: tKey("run:tabs.reports") },
];

const PHASE_MESSAGES = {
  preparing: tKey("run:phase.preparing"),
  "loading-data": tKey("run:phase.loading-data"),
  // Neutral on purpose: on the upload fallback it is the browser doing the
  // downloading, and only `deployProgress` tells the two paths apart.
  "downloading-model": tKey("run:phase.downloading-model"),
  "uploading-video": tKey("run:phase.uploading-video"),
  "removing-video": tKey("run:phase.removing-video"),
  deploying: tKey("run:phase.deploying"),
  stopping: tKey("run:phase.stopping"),
} satisfies Record<Exclude<DeployPhase, "idle">, I18nKey<"run">>;

const formatMegabytes = (bytes: number): string =>
  `${(bytes / 1_000_000).toFixed(0)} MB`;

/**
 * What to show while the camera fetches. Falls back to the bare phase label
 * whenever there is nothing honest to add — no progress at all on the upload
 * path, and no byte totals when the artifacts could not be sized.
 */
const progressLabel = (
  t: TFunction<["run", "common"]>,
  phase: DeployPhase,
  progress: DeployProgress | null | undefined,
): string => {
  const base = t(PHASE_MESSAGES[phase as Exclude<DeployPhase, "idle">]);

  if (!progress || phase !== "downloading-model") return base;

  // Every model was already on the camera, so there is nothing to show a
  // percentage for — and saying "0 of 0 MB" would read as a stall.
  if (progress.allCached) return t("run:progress.allCached");

  if (progress.fraction === null) return t("run:progress.downloading");

  return t("run:progress.downloadingPercent", {
    percent: Math.round(progress.fraction * 100),
    downloaded: formatMegabytes(progress.bytesDownloaded),
    total: formatMegabytes(progress.bytesTotal),
  });
};

export const RunSubheader = ({
  activeTab,
  onTabChange,
  onDeploy,
  onStop,
  deployPhase,
  deployProgress,
  canDeploy,
  hasModel,
  isDeployed,
  configSelector,
}: RunSubheaderProps) => {
  const { t } = useTranslation(["run", "common"]);
  const isDeploying = deployPhase !== "idle";
  // `reporting` off removes the Reports tab; `restricted` (payment overdue)
  // removes the controls that would deploy to the camera, but not Stop.
  const { reporting, restricted } = useFeatureFlags();
  const tabs = reporting ? TABS : TABS.filter((tab) => tab.key !== "reports");

  return (
    <div className="flex h-12 flex-shrink-0 items-end justify-between border-b border-border bg-gray-100 pr-6">
      <Tabs
        value={activeTab}
        onValueChange={(v) => v && onTabChange(v as RunTab)}
        className="gap-0"
      >
        <TabsList variant="line" className="border-b-0! px-6">
          {tabs.map((tab) => (
            <TabsTrigger key={tab.key} value={tab.key}>
              {t(tab.label)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="flex h-full items-center gap-2">
        {isDeploying && (
          <>
            <div
              className="flex items-center gap-2 text-sm text-muted-foreground"
              role="status"
              aria-live="polite"
            >
              <Spinner className="size-4 text-emerald-500" />
              <span>{progressLabel(t, deployPhase, deployProgress)}</span>
            </div>
            <div className="h-4 w-px bg-border" />
          </>
        )}
        {!restricted && configSelector && (
          <>
            {configSelector}
            <div className="h-4 w-px bg-border" />
          </>
        )}
        {/* Stop survives `restricted`: the relay permits the DELETEs behind it,
            and an organization that cannot pay still has to be able to halt a
            running inspection. `isDeployed` comes from a GET, which is never
            blocked, so the disabled state stays truthful. */}
        <Button
          size="sm"
          variant="outline"
          onClick={onStop}
          disabled={!isDeployed || isDeploying}
        >
          <Square className="size-4" />
          {t("subheader.stop")}
        </Button>
        {!restricted && (
          <Tooltip>
            {/* Wrapped in a span so the tooltip still fires while the button
                is disabled — a disabled button emits no pointer events. */}
            <TooltipTrigger render={<span />}>
              <Button
                size="sm"
                onClick={onDeploy}
                disabled={!canDeploy || isDeploying}
              >
                <Play className="size-4" />
                {t("subheader.deploy")}
              </Button>
            </TooltipTrigger>
            {!hasModel && <TooltipContent>{t(NO_MODEL_MESSAGE)}</TooltipContent>}
          </Tooltip>
        )}
      </div>
    </div>
  );
};
